import { Alert, Badge, Button, EmptyState, Spinner, TextField, useToast } from '@cartly/ui';
import { Plus, Trash2, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useSession } from '../../app/session';

interface Note {
  id: string;
  body: string;
  author: string | null;
  createdAt: string;
}
interface TimelineItem {
  type: string;
  at: string;
  title: string;
  detail: string | null;
  ref: string;
}

/** Tags, notes and the activity timeline of one customer. */
export function CustomerCrm({ customerId }: { customerId: string }) {
  const { can } = useSession();
  const toast = useToast();
  const manage = can('customers:manage');
  const base = `/crm/customers/${customerId}`;
  const tags = useLoader(
    () => api.get<{ tags: string[] }>(`${base}/tags`).then((r) => r.tags),
    customerId,
  );
  const notes = useLoader(
    () => api.get<{ notes: Note[] }>(`${base}/notes`).then((r) => r.notes),
    customerId,
  );
  const timeline = useLoader(
    () => api.get<{ items: TimelineItem[] }>(`${base}/timeline`).then((r) => r.items),
    customerId,
  );
  const tagSubmit = useSubmit();
  const noteSubmit = useSubmit();
  const [tag, setTag] = useState('');
  const [note, setNote] = useState('');

  const refresh = () => {
    void tags.reload();
    void notes.reload();
    void timeline.reload();
  };

  const addTag = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await tagSubmit.run(async () => {
      await api.post(`${base}/tags`, { tag });
      return true;
    });
    if (ok) {
      setTag('');
      refresh();
    }
  };
  const removeTag = async (t: string) => {
    try {
      await api.delete(`${base}/tags/${encodeURIComponent(t)}`);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const addNote = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await noteSubmit.run(async () => {
      await api.post(`${base}/notes`, { body: note });
      return true;
    });
    if (ok) {
      setNote('');
      refresh();
    }
  };
  const removeNote = async (id: string) => {
    try {
      await api.delete(`${base}/notes/${id}`);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack">
      <h3 className="section-title">Tags</h3>
      <div className="row">
        {(tags.data ?? []).length === 0 && <span className="text-muted">No tags</span>}
        {(tags.data ?? []).map((t) => (
          <Badge key={t}>
            {t}
            {manage && (
              <button
                type="button"
                className="badge__remove"
                aria-label={`Remove tag ${t}`}
                onClick={() => void removeTag(t)}
              >
                <X size={12} />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {manage && (
        <form className="row" onSubmit={(e) => void addTag(e)}>
          <TextField
            label="Add tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            error={tagSubmit.error ?? tagSubmit.fieldErrors['tag']}
            maxLength={50}
          />
          <Button
            type="submit"
            variant="secondary"
            icon={<Plus size={14} />}
            loading={tagSubmit.pending}
            disabled={!tag.trim()}
          >
            Add
          </Button>
        </form>
      )}

      <h3 className="section-title">Notes</h3>
      {notes.loading && !notes.data ? (
        <Spinner />
      ) : (notes.data ?? []).length === 0 ? (
        <p className="text-muted">No notes yet.</p>
      ) : (
        <ul className="stack">
          {notes.data!.map((n) => (
            <li key={n.id} className="row">
              <div>
                <div>{n.body}</div>
                <div className="text-muted text-sm">
                  {n.author ?? 'Someone'} · {formatRelative(n.createdAt)}
                </div>
              </div>
              {manage && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Trash2 size={14} />}
                  aria-label="Delete note"
                  onClick={() => void removeNote(n.id)}
                >
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {manage && (
        <form className="stack" onSubmit={(e) => void addNote(e)}>
          {noteSubmit.error && <Alert tone="critical">{noteSubmit.error}</Alert>}
          <TextField
            label="Add note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
          <div>
            <Button
              type="submit"
              variant="secondary"
              loading={noteSubmit.pending}
              disabled={!note.trim()}
            >
              Save note
            </Button>
          </div>
        </form>
      )}

      <h3 className="section-title">Timeline</h3>
      {timeline.loading && !timeline.data ? (
        <Spinner />
      ) : (timeline.data ?? []).length === 0 ? (
        <EmptyState title="No activity yet">
          Orders, notes and WhatsApp messages appear here.
        </EmptyState>
      ) : (
        <ul className="stack">
          {timeline.data!.map((i) => (
            <li key={`${i.type}:${i.ref}`}>
              <div>{i.title}</div>
              {i.detail && <div className="text-muted text-sm">{i.detail}</div>}
              <div className="text-muted text-sm">{formatRelative(i.at)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
